import { themeColors } from 'app/shared/theme';
import { INotificationEntity } from 'types/general';
import MinusSquare from 'assets/minus_square.svg?react';
import PlusSquare from 'assets/plus_square.svg?react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStyles } from './PlantCrossShippingItem.styles';
import { Grow } from '@mui/material';
import { Table } from 'app/shared/components/generic-ui/Table/Table';
import { CellSize } from 'app/shared/components/generic-ui/Table/Table.types';
import { CellAlignment } from 'app/shared/components/generic-ui/Table/Table.types';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
interface IProps {
  notificationEntities: INotificationEntity[];
}

const timeoutOpen = 100;
const timeoutClose = 300;

export const PlantCrossShippingItem = ({ notificationEntities }: IProps) => {
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { classes } = useStyles();
  const [containerClass, setContainerClass] = useState(
    isOpen ? classes.container : classes.containerClosed,
  );
  const { t } = useTranslation();

  const spoilerClickHandler = useCallback(() => {
    setIsOpen(prevState => !prevState);
  }, []);

  const columns = useMemo(() => {
    return [
      {
        id: 'externalId',
        label: t(translations.notificationSettingsPage.id),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'name',
        label: t(translations.notificationSettingsPage.name, {
          entity: 'Plant',
        }),
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
    ];
  }, [t]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isOpen) {
      setContainerClass(classes.container);
      timeoutId = setTimeout(() => {
        setIsReady(true);
      }, timeoutOpen);
    } else {
      setIsReady(false);

      timeoutId = setTimeout(() => {
        setContainerClass(classes.containerClosed);
      }, timeoutClose);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [classes, isOpen]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {`${notificationEntities
          .slice(0, 5)
          .map((el: any) => el.name)
          .join(', ')}${notificationEntities.length > 5 ? `...` : ''}`}
        {isOpen && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MinusSquare
              stroke={themeColors.greyscale1}
              className={classes.minusIcon}
              data-testid="minus"
              onClick={spoilerClickHandler}
            />
          </div>
        )}
        {!isOpen && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlusSquare
              stroke={themeColors.greyscale1}
              className={classes.plusIcon}
              data-testid="plus"
              onClick={spoilerClickHandler}
            />
          </div>
        )}
      </div>
      <Grow in={isReady}>
        <div
          className={containerClass}
          style={{
            marginBottom: isOpen ? '10px' : '0px',
          }}
        >
          <Table
            withHover={true}
            withPagination={false}
            columns={columns}
            tableData={notificationEntities}
            withCheckbox={false}
            emptyTableText={`${t(translations.notificationSettingsPage.noData, {
              entity: 'Plant',
            })}.`}
            rowsTotal={notificationEntities?.length}
          />
        </div>
      </Grow>
    </div>
  );
};
